/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Stack,
  Link,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { MuiTelInput } from 'mui-tel-input';
import { useRegister, sendRegistrationOTP, verifyRegistrationOTP, useEmailCheck } from '@/api/auth';
import { registerSchema, RegisterFormData } from '@/validations/auth';


type RegistrationOption = {
  value: string;
  label: string;
  labelZh?: string;
  labelCn?: string;
};

type RegistrationOptions = {
  genders: RegistrationOption[];
  months: RegistrationOption[];
};

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      verificationCode: '',
      password: '',
      confirmPassword: '',
      phone: undefined,
      address: undefined,
      city: undefined,
      state: undefined,
      country: undefined,
      zipCode: undefined,
      birthMonth: '',
      birthDay: '',
      birthYear: undefined,
      gender: undefined,
      referrer: undefined,
      marketingConsent: false,
      termsAccepted: false,
      privacyPolicyAccepted: false,
      dataCollectionConsent: false,
      emailNotifications: false,
      mobileAppPush: false,
    },
  });

  const { register: registerUser, isLoading, error } = useRegister();
  const { checkEmail, isLoading: isCheckingEmail } = useEmailCheck();
  
  // Static registration options with translations
  const staticOptions: RegistrationOptions = {
    genders: [
      {
        value: "male",
        label: "Male",
        labelZh: "男",
        labelCn: "男"
      },
      {
        value: "female",
        label: "Female",
        labelZh: "女",
        labelCn: "女"
      }
    ],
    months: [
      { value: "01", label: "January", labelZh: "一月", labelCn: "一月" },
      { value: "02", label: "February", labelZh: "二月", labelCn: "二月" },
      { value: "03", label: "March", labelZh: "三月", labelCn: "三月" },
      { value: "04", label: "April", labelZh: "四月", labelCn: "四月" },
      { value: "05", label: "May", labelZh: "五月", labelCn: "五月" },
      { value: "06", label: "June", labelZh: "六月", labelCn: "六月" },
      { value: "07", label: "July", labelZh: "七月", labelCn: "七月" },
      { value: "08", label: "August", labelZh: "八月", labelCn: "八月" },
      { value: "09", label: "September", labelZh: "九月", labelCn: "九月" },
      { value: "10", label: "October", labelZh: "十月", labelCn: "十月" },
      { value: "11", label: "November", labelZh: "十一月", labelCn: "十一月" },
      { value: "12", label: "December", labelZh: "十二月", labelCn: "十二月" }
    ]
  };
  
  const options = staticOptions;
  const navigate = useNavigate();
  
  // Function to get translated label based on current language
  const getTranslatedLabel = (option: RegistrationOption): string => {
    const currentLanguage = i18n.language;
    switch (currentLanguage) {
      case 'zh':
        return option.labelZh || option.label;
      case 'cn':
        return option.labelCn || option.label;
      default:
        return option.label;
    }
  };

  // OTP related state
  const [_, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [__, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  // Email validation state
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState<string>('');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Ref to store the checkEmail function to avoid dependency issues
  const checkEmailRef = useRef(checkEmail);
  checkEmailRef.current = checkEmail;

  // Countdown timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check email existence function
  const handleEmailCheck = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailExists(null);
      setEmailCheckMessage('');
      return;
    }

    try {
      const response = await checkEmailRef.current(email);
      setEmailExists(response.exists);
      setEmailCheckMessage(response.message || '');
    } catch (error: unknown) {
      setEmailExists(null);
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { message?: string } } };
        setEmailCheckMessage(apiError.response?.data?.message || 'Failed to check email');
      } else {
        setEmailCheckMessage('Failed to check email');
      }
    }
  }, []); // Empty dependency array since we use ref

  // Email check effect with debouncing
  const emailValue = watch('email');
  useEffect(() => {
    if (!emailValue || !emailValue.includes('@')) {
      setEmailExists(null);
      setEmailCheckMessage('');
      return;
    }

    const timeoutId = setTimeout(() => {
      handleEmailCheck(emailValue);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [emailValue, handleEmailCheck]); // Now safe to include handleEmailCheck

  // Send OTP function
  const handleSendOTP = async () => {
    const email = watch('email');
    if (!email) {
      setOtpError('Please enter your email first');
      return;
    }

    setSendingOtp(true);
    setOtpError(null);
    
    try {
      await sendRegistrationOTP(email);
      setOtpSent(true);
      setCountdown(90); // 90 seconds countdown
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { message?: string } } };
        setOtpError(apiError.response?.data?.message || 'Failed to send verification code');
      } else {
        setOtpError('Failed to send verification code');
      }
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify OTP function
  const handleVerifyOTP = async (verificationCode: string) => {
    const email = watch('email');
    if (!email || !verificationCode) return;

    setVerifyingOtp(true);
    setOtpError(null);
    setOtpVerified(false); // Reset verification status

    try {
      const response = await verifyRegistrationOTP(email, verificationCode);
      
      // Check if the response indicates valid verification
      if (response && response.valid === true) {
        setOtpVerified(true);
        setOtpError(null);
      } else {
        // Handle invalid verification code response
        setOtpVerified(false);
        setOtpError(response?.error || 'Invalid verification code');
      }
    } catch (error: unknown) {
      setOtpVerified(false);
      // Handle API error response format
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { error?: string; message?: string } } };
        if (apiError.response?.data?.error) {
          setOtpError(apiError.response.data.error);
        } else if (apiError.response?.data?.message) {
          setOtpError(apiError.response.data.message);
        } else {
          setOtpError('Invalid verification code');
        }
      } else {
        setOtpError('Invalid verification code');
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  const watchedTerms = watch([
    'termsAccepted',
    'privacyPolicyAccepted',
    'dataCollectionConsent',
    'marketingConsent',
    'emailNotifications',
    'mobileAppPush',
  ]);

  const handleTermsChange =
    (name: keyof RegisterFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setValue(name, isChecked);

      // Hierarchical checkbox logic
      if (name === 'marketingConsent') {
        // When marketing consent is checked/unchecked, check/uncheck email and mobile app push
        setValue('emailNotifications', isChecked);
        setValue('mobileAppPush', isChecked);
      } else if (name === 'emailNotifications' || name === 'mobileAppPush') {
        // When email or mobile app push is checked, check marketing consent
        if (isChecked) {
          setValue('marketingConsent', true);
        } else {
          // If both email and mobile app push are unchecked, uncheck marketing consent
          const emailChecked = name === 'emailNotifications' ? false : watch('emailNotifications');
          const mobileChecked = name === 'mobileAppPush' ? false : watch('mobileAppPush');
          if (!emailChecked && !mobileChecked) {
            setValue('marketingConsent', false);
          }
        }
      }
    };

  const handleAgreeAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    // Set all 6 checkboxes to the same value
    setValue('termsAccepted', isChecked);
    setValue('privacyPolicyAccepted', isChecked);
    setValue('dataCollectionConsent', isChecked);
    setValue('marketingConsent', isChecked);
    setValue('emailNotifications', isChecked);
    setValue('mobileAppPush', isChecked);
  };

  const handleGenderChange = (event: SelectChangeEvent) => {
    setValue('gender', event.target.value as string);
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    const monthValue = event.target.value;
    setValue('birthMonth', monthValue || '');
  };

  const onSubmit = handleSubmit(async data => {
    if (!otpVerified) {
      setOtpError('Please verify your email first');
      return;
    }

    try {
      // Transform the data to match the API format
      const apiData = {
        ...data,
        name: `${data.firstName} ${data.lastName}`,
        verificationCode: data.verificationCode,
        // Convert string values to numbers for API
        birthMonth: data.birthMonth ? parseInt(data.birthMonth, 10) : undefined,
        birthDay: data.birthDay ? parseInt(data.birthDay, 10) : undefined,
      };

      // Type assertion for API data (birthMonth and birthDay are now numbers)
      await registerUser(apiData as unknown as RegisterFormData & { 
        name: string;
        verificationCode: string;
        birthMonth?: number;
        birthDay?: number;
      });
      // Handle successful registration (e.g., redirect to login or dashboard)
      navigate('/sign-in');
    } catch (error: unknown) {
      console.error('Registration failed:', error);
      // Firebase errors are now handled gracefully in the backend
      // Only show backend registration errors to users
    }
  });

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 4,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
          {t('auth.join')}
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          fontWeight={700}
          sx={{ mb: 0.5 }}
        >
          {t('benefits.dontMissOut')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 2 }}>
          {t('benefits.hatsuhikenBenefits')}
        </Typography>
        
        {/* API Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_b_day_off.svg"
              alt="B-DAY"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.custom.brown, fontWeight: 500, mt: 1 }}
            >
              {t('benefits.bdayOff')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_app_point.svg"
              alt="Points"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.custom.brown, fontWeight: 500, mt: 1 }}
            >
              {t('benefits.appInstallPoints')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_coupon.svg"
              alt="Coupon"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
            >
              {t('benefits.hk10OffCoupon')}
            </Typography>
          </Box>
        </Box>
        <Typography align="center" sx={{ mb: 2 }}>
          {t('brand.enjoyExclusiveServices')}
          <br />
          <b>{t('brand.fromHatsuhiken')}</b>
        </Typography>
        <form onSubmit={onSubmit}>
          <Stack spacing={'12px'}>
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.firstName')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('firstName')}
              placeholder={t('auth.firstName')}
              required
              fullWidth
              size="small"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              {...register('lastName')}
              placeholder={t('auth.lastName')}
              required
              fullWidth
              size="small"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.emailId')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('email')}
              placeholder={t('auth.emailHelper')}
              required
              fullWidth
              size="small"
              error={!!errors.email || emailExists === true}
              helperText={
                errors.email?.message || 
                (emailExists === false ? t('auth.errors.emailAvailable') : '') ||
                (isCheckingEmail ? t('auth.errors.checkingEmail') : '')
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: emailExists === true ? '#ffebee' : 'transparent',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: emailExists === true ? '#d32f2f' : undefined,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: emailExists === true ? '#d32f2f' : undefined,
                  },
                },
              }}
            />
            {emailCheckMessage && (
              <Alert 
                severity={emailExists === true ? 'error' : emailExists === false ? 'success' : 'info'}
                sx={{ mt: 1 }}
              >
                {emailCheckMessage}
              </Alert>
            )}
            
            {/* Verification Code Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Typography fontWeight={700} fontSize="16px">
                   {t('auth.verificationCode')} <span style={{ color: '#d32f2f' }}>*</span>
                 </Typography>
               </Box>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                 <TextField
                   {...register('verificationCode')}
                   placeholder={t('auth.verificationCodePlaceholder')}
                   fullWidth
                   size="small"
                   InputProps={{
                     endAdornment: otpVerified && (
                       <Box sx={{ 
                         display: 'flex', 
                         alignItems: 'center', 
                         color: '#4caf50',
                         fontSize: '14px',
                         mr: 1
                       }}>
                         <Box sx={{ 
                           width: 16, 
                           height: 16, 
                           borderRadius: '50%', 
                           bgcolor: '#4caf50', 
                           display: 'flex', 
                           alignItems: 'center', 
                           justifyContent: 'center',
                           mr: 0.5
                         }}>
                           <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                         </Box>
                       </Box>
                     )
                   }}
                   inputProps={{ 
                     maxLength: 6,
                     style: { textAlign: 'center', fontSize: '14px', fontWeight: 'bold' }
                   }}
                   onChange={(e) => {
                     const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                     setValue('verificationCode', value);
                     // Clear error when user starts typing
                     if (otpError) {
                       setOtpError(null);
                     }
                     if (value.length === 6) {
                       handleVerifyOTP(value);
                     }
                   }}
                   sx={{
                     '& .MuiOutlinedInput-root': {
                       ...(otpVerified && {
                         '& fieldset': { borderColor: '#4caf50' },
                         '&:hover fieldset': { borderColor: '#4caf50' },
                         '&.Mui-focused fieldset': { borderColor: '#4caf50' },
                       }),
                       ...(otpError && !otpVerified && {
                         '& fieldset': { borderColor: '#d32f2f' },
                         '&:hover fieldset': { borderColor: '#d32f2f' },
                         '&.Mui-focused fieldset': { borderColor: '#d32f2f' },
                       }),
                     },
                   }}
                 />
                {!otpVerified && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSendOTP}
                    disabled={sendingOtp || countdown > 0 || !watch('email') || emailExists === true}
                    sx={{
                      minWidth: '120px',
                      height: '36px',
                      fontSize: '12px',
                      bgcolor: theme.palette.custom.brown,
                      color: 'white',
                      fontWeight: 700,
                      '&:hover': { 
                        bgcolor: '#a98c74' 
                      },
                      '&:disabled': {
                        bgcolor: '#ccc',
                        color: '#999',
                      },
                    }}
                  >
                    {sendingOtp ? t('common.loading') : countdown > 0 ? `${countdown}s` : t('auth.sendCode')}
                  </Button>
                )}
              </Box>
              
              {otpVerified && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: '#4caf50',
                  fontSize: '14px',
                  mt: 0.5
                }}>
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    bgcolor: '#4caf50', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 0.5
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                  </Box>
                  {t('auth.verificationCodeValid')}
                </Box>
              )}
              
              {otpError && !otpVerified && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  color: '#d32f2f',
                  fontSize: '14px',
                  mb: 0.5
                }}>
                  <Box sx={{ 
                    width: 16, 
                    height: 16, 
                    borderRadius: '50%', 
                    bgcolor: '#d32f2f', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    mr: 0.5
                  }}>
                    <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✗</Typography>
                  </Box>
                  {otpError}
                </Box>
              )}
            </Box>
            
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.phoneNumber')} <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MuiTelInput
                  {...field}
                  value={field.value || ''}
                  fullWidth
                  size="small"
                  variant="outlined"
                  sx={{ 
                    mb: 1,
                    '& .MuiTelInput-flagButton': {
                      borderColor: '#E1DACA',
                    },
                    '& .MuiTelInput-flagButton:hover': {
                      borderColor: '#E1DACA',
                    },
                    '& .MuiTelInput-flagButton.Mui-focused': {
                      borderColor: '#E1DACA',
                    },
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  defaultCountry="HK"
                  forceCallingCode={true}
                  disableFormatting={false}
                  placeholder={t('auth.phoneNumberPlaceholder')}
                />
              )}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.password')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('password')}
              placeholder={t('auth.passwordHelper')}
              required
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.confirmPassword')}{' '}
              <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('confirmPassword')}
              placeholder={t('auth.confirmPassword')}
              required
              fullWidth
              size="small"
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('register.birthday')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Controller
                  name="birthMonth"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      onChange={handleMonthChange}
                      displayEmpty
                      error={!!errors.birthMonth}
                    >
                      <MenuItem value="" disabled>
                        {t('register.selectMonth')}
                      </MenuItem>
                      {options?.months?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {getTranslatedLabel(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.birthMonth && (
                  <FormHelperText error>
                    {errors.birthMonth.message}
                  </FormHelperText>
                )}
              </FormControl>
              <TextField
                {...register('birthDay')}
                placeholder={t('register.selectDay')}
                size="small"
                sx={{ flex: 1 }}
                type="text"
                inputProps={{ maxLength: 2 }}
                error={!!errors.birthDay}
                helperText={errors.birthDay?.message}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers and limit to 2 digits
                  if (/^\d{0,2}$/.test(value)) {
                    setValue('birthDay', value);
                  }
                }}
              />

            </Box>
            <Typography variant="caption" sx={{ mt: 0, mb: 0 }}>
              {t('register.birthdayCoupon')}
            </Typography>
            <Typography fontWeight={700} fontSize="16px">
              {t('register.gender')}
            </Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="gender"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    onChange={handleGenderChange}
                    displayEmpty
                    error={!!errors.gender}
                  >
                    <MenuItem value="" disabled>
                      {t('register.selectGender')}
                    </MenuItem>
                      {options?.genders?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {getTranslatedLabel(option)}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              {errors.gender && (
                <FormHelperText error>{errors.gender.message}</FormHelperText>
              )}
            </FormControl>
            <Typography fontWeight={700} fontSize="16px">
              {t('register.referrer')}
            </Typography>
            <TextField
              {...register('referrer')}
              placeholder={t('register.referrerHelper')}
              fullWidth
              size="small"
              error={!!errors.referrer}
              helperText={errors.referrer?.message}
            />
          </Stack>
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    watchedTerms[0] &&
                    watchedTerms[1] &&
                    watchedTerms[2] &&
                    watchedTerms[3] &&
                    watchedTerms[4] &&
                    watchedTerms[5]
                  }
                  onChange={handleAgreeAllChange}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  {t('register.agreeAll')}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[0]}
                  onChange={handleTermsChange('termsAccepted')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    <Link
                      href="/page/terms-and-policy"
                      target="_blank"
                      underline="always"
                      sx={{ color: '#3D3C3C', textDecoration: 'underline' }}
                    >
                      {t('register.termsOfUse')}
                    </Link>{' '}
                    {t('register.termsRequired')}
                  </Typography>
                </span>
              }
            />
            {errors.termsAccepted && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', ml: 4 }}
              >
                {errors.termsAccepted.message}
              </Typography>
            )}
            {errors.privacyPolicyAccepted && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', ml: 4 }}
              >
                {errors.privacyPolicyAccepted.message}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[2]}
                  onChange={handleTermsChange('dataCollectionConsent')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    {t('register.personalOptional')}
                  </Typography>
                  <br />
                  <Link
                    href="/page/collection-information-policy"
                    target="_blank"
                    underline="always"
                    variant="caption"
                    sx={{
                      color: theme.palette.custom.brown,
                      textDecoration: 'underline',
                      textDecorationColor: theme.palette.custom.brown,
                      fontSize: '14px',
                    }}
                  >
                    {t('register.readMore')}
                  </Link>
                </span>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[3]}
                  onChange={handleTermsChange('marketingConsent')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    {t('register.marketing')}
                  </Typography>
                  <br />
                  <Link
                    href="/page/marketing-policy"
                    target="_blank"
                    underline="always"
                    variant="caption"
                    sx={{
                      color: theme.palette.custom.brown,
                      textDecoration: 'underline',
                      textDecorationColor: theme.palette.custom.brown,
                      fontSize: '14px',
                    }}
                  >
                    {t('register.readMore')}
                  </Link>
                </span>
              }
            />
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watchedTerms[4]}
                    onChange={handleTermsChange('emailNotifications')}
                    size="small"
                    sx={{
                      color: '#3D3C3C',
                      '&.Mui-checked': { color: '#3D3C3C' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">{t('register.email')}</Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watchedTerms[5]}
                    onChange={handleTermsChange('mobileAppPush')}
                    size="small"
                    sx={{
                      color: '#3D3C3C',
                      '&.Mui-checked': { color: '#3D3C3C' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('register.mobileAppPush')}
                  </Typography>
                }
              />
            </Stack>
          </Box>
          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 2, textAlign: 'center' }}
            >
              {error.message}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !otpVerified || emailExists === true}
            sx={{
              bgcolor: theme.palette.custom.brown,
              color: 'white',
              fontWeight: 700,
              mt: 3,
              '&:hover': { bgcolor: '#a98c74' },
            }}
          >
            {isLoading ? t('common.loading') : t('benefits.joinNow')}
          </Button>
        </form>
      </Box>
    </Container>
  );
}
